import {
	Extension,
	ExtensionProperty,
	type IProperty,
	type Nullable,
	PropertyType,
	type ReaderContext,
	type WriterContext,
} from "@gltf-transform/core";

import { LoopOnce as LOOP_ONCE, LoopRepeat as LOOP_REPEAT } from "three";

export const EXTENSION_NAME = "DPL_animation_loop";

export interface IAnimationLoop extends IProperty {
	playback: "repeat" | "once";
}

export const ANIMATION_PLAYBACK_VALUES: Record<
	"once" | "repeat",
	typeof LOOP_ONCE | typeof LOOP_REPEAT
> = {
	once: LOOP_ONCE,
	repeat: LOOP_REPEAT,
};

export class AnimationLoop extends ExtensionProperty<IAnimationLoop> {
	static EXTENSION_NAME = EXTENSION_NAME;
	extensionName = EXTENSION_NAME;
	propertyType = "AnimationLoop";
	parentTypes = [PropertyType.ANIMATION];

	protected init() {
		this.extensionName = EXTENSION_NAME;
		this.propertyType = "AnimationLoop";
		this.parentTypes = [PropertyType.ANIMATION];
	}

	protected getDefaults(): Nullable<IAnimationLoop> {
		return Object.assign(super.getDefaults() as IProperty, {
			playback: "once" as const,
		});
	}

	setPlayback(playback: "repeat" | "once") {
		return this.set("playback", playback);
	}

	getPlaybackValue(): "repeat" | "once" {
		return this.get("playback");
	}
}

export class DPLAnimationLoopExtension extends Extension {
	static EXTENSION_NAME = EXTENSION_NAME;
	extensionName = EXTENSION_NAME;
	prereadTypes = [PropertyType.ANIMATION];
	prewriteTypes = [PropertyType.ANIMATION];

	createAnimationLoop() {
		return new AnimationLoop(this.document.getGraph());
	}

	read(_context: ReaderContext): this {
		return this;
	}

	write(context: WriterContext): this {
		for (const animation of this.document.getRoot().listAnimations()) {
			const animationLoopProperties =
				animation.getExtension<AnimationLoop>(EXTENSION_NAME);
			const animationIndex = context.animationIndexMap.get(animation);

			if (!animationLoopProperties || animationIndex === undefined) {
				continue;
			}

			const animationDef = context.jsonDoc.json.animations?.[animationIndex];
			if (!animationDef) {
				continue;
			}

			animationDef.extensions ??= {};

			animationDef.extensions[EXTENSION_NAME] = {
				playback: animationLoopProperties.getPlaybackValue(),
			};
		}
		return this;
	}
}
